import type { SchedulingApiEnvelope } from "./workspace-scheduling.types";

export type SchedulePublicEventTypeMode =
  | "one_on_one"
  | "group"
  | "collective"
  | "round_robin"
  | "routing"
  | string;

export type SchedulePublicAssignmentStrategy =
  | "round_robin_equal_distribution"
  | "round_robin_priority"
  | "round_robin_weighted"
  | "least_recently_booked"
  | "manual_assignment"
  | string;

export type SchedulePublicBookingWindowType = "rolling" | "fixed" | string;
export type SchedulePublicTimezoneMode =
  | "invitee_detected"
  | "event_timezone"
  | "host_timezone"
  | string;
export type SchedulePublicConfirmationMode = "auto" | "manual" | string;
export type SchedulePublicQuestionFieldType =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "number"
  | "select"
  | "multi_select"
  | "checkbox"
  | "radio"
  | "boolean"
  | "date"
  | string;

export type SchedulePublicJsonValue =
  | string
  | number
  | boolean
  | null
  | SchedulePublicJsonValue[]
  | { [key: string]: SchedulePublicJsonValue };
export type SchedulePublicJsonRecord = Record<string, SchedulePublicJsonValue>;

export interface SchedulePublicWorkspace {
  id: number;
  name: string;
  slug: string;
  displayName?: string | null;
  timezone: string;
  defaultLocale?: string | null;
  defaultCurrency?: string | null;
  brandColor?: string | null;
  customDomain?: string | null;
  logoUrl?: string | null;
}

export interface SchedulePublicHost {
  id: number;
  userId: number;
  availabilityScheduleId: number;
  hostRole: string;
  priority: number;
  weight: number;
  isRequired: boolean;
  userName?: string | null;
  userEmail?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  availabilityScheduleName?: string | null;
  availabilityTimezone?: string | null;
  availabilityIsDefault?: boolean | null;
}

export interface SchedulePublicLocation {
  id: number;
  locationType: string;
  locationValue?: string | null;
  providerPlatformId?: number | null;
  isDefault: boolean;
  sortOrder: number;
  settings?: SchedulePublicJsonRecord | null;
}

export interface SchedulePublicQuestion {
  id: number;
  label: string;
  fieldKey: string;
  fieldType: SchedulePublicQuestionFieldType;
  placeholder?: string | null;
  helpText?: string | null;
  isRequired: boolean;
  isHidden?: boolean;
  mapToCrmField?: string | null;
  options?: SchedulePublicJsonRecord | SchedulePublicJsonValue[] | null;
  validationRules?: SchedulePublicJsonRecord | null;
  sortOrder: number;
}

export interface SchedulePublicBookingLinkSettings {
  showEventDescription?: boolean;
  showHostNameBeforeBooking?: boolean;
  allowInviteeTimezoneSelection?: boolean;
  [key: string]: SchedulePublicJsonValue | undefined;
}

export interface SchedulePublicBookingLink {
  eventTypeId: number;
  publicKey: string;
  name: string;
  slug: string;
  description?: string | null;
  eventTypeMode: SchedulePublicEventTypeMode;
  assignmentStrategy?: SchedulePublicAssignmentStrategy | null;
  durationMinutes: number;
  slotIntervalMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minimumBookingNoticeMinutes: number;
  firstSlotOffsetMinutes: number;
  onlyShowFirstAvailableSlot: boolean;
  bookingWindowType: SchedulePublicBookingWindowType;
  bookingWindowDays?: number | null;
  bookingWindowStartDate?: string | null;
  bookingWindowEndDate?: string | null;
  timezoneMode: SchedulePublicTimezoneMode;
  timezone: string;
  lockTimezone: boolean;
  confirmationMode: SchedulePublicConfirmationMode;
  allowCancellation: boolean;
  allowReschedule: boolean;
  cancellationCutoffMinutes: number;
  rescheduleCutoffMinutes: number;
  requireAuthentication: boolean;
  requireEmailVerification: boolean;
  maxInvitees: number;
  seatsPerSlot: number;
  showRemainingSeats: boolean;
  paymentRequired: boolean;
  successRedirectUrl?: string | null;
  cancelRedirectUrl?: string | null;
  embedEnabled: boolean;
  settings?: SchedulePublicBookingLinkSettings | null;
  workspace: SchedulePublicWorkspace;
  hosts: SchedulePublicHost[];
  locations: SchedulePublicLocation[];
  questions: SchedulePublicQuestion[];
}

export interface SchedulePublicAvailableSlot {
  startTime: string;
  endTime: string;
  available: boolean;
  availableHostIds: number[];
  remainingSeats?: number | null;
}

export interface SchedulePublicAvailableSlotsData {
  eventTypeId: number;
  publicKey: string;
  date: string;
  timezone: string;
  durationMinutes: number;
  slotIntervalMinutes: number;
  eventTypeMode: SchedulePublicEventTypeMode;
  slots: SchedulePublicAvailableSlot[];
}

export interface SchedulePublicAvailableDatesData {
  eventTypeId: number;
  publicKey: string;
  from: string;
  to: string;
  timezone: string;
  availableDates: string[];
}

export interface SchedulePublicReserveSlotRequest {
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  source: string;
  medium: string;
  campaign: string;
  inviteeEmail: string;
  inviteeName?: string;
  inviteePhone?: string;
  inviteeTimezone?: string;
  crmContactId?: number | null;
}

export interface SchedulePublicReserveSlotInput
  extends SchedulePublicReserveSlotRequest {
  publicKey: string;
}

export interface SchedulePublicReserveSlotData {
  lockKey?: string;
  eventTypeId?: number;
  hostUserId?: number;
  publicKey?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  reservationId?: string | number;
  expiresAt?: string | null;
  [key: string]: SchedulePublicJsonValue | undefined;
}

export interface SchedulePublicBookingInviteeInput {
  name: string;
  email: string;
  phone: string;
  timezone: string;
  crmContactId: number | null;
  metadata: SchedulePublicJsonRecord;
}

export interface SchedulePublicBookingAnswerInput {
  questionId: number;
  fieldKey: string;
  answerValue: string;
  answerJson: SchedulePublicJsonRecord;
}

export interface SchedulePublicCreateBookingRequest {
  publicKey: string;
  lockKey: string;
  inviteeTimezone: string;
  source: string;
  medium: string;
  campaign: string;
  bookingChannel: string;
  routingFormSubmissionId: number | null;
  invitees: SchedulePublicBookingInviteeInput[];
  answers: SchedulePublicBookingAnswerInput[];
  verificationTokenId?: number;
  verificationCode?: string;
}

export type SchedulePublicCreateBookingInput =
  SchedulePublicCreateBookingRequest;

export interface SchedulePublicSendEmailVerificationRequest {
  publicKey: string;
  email: string;
}

export type SchedulePublicSendEmailVerificationInput =
  SchedulePublicSendEmailVerificationRequest;

export interface SchedulePublicEmailVerificationData {
  verificationTokenId: number;
  expiresAt?: string | null;
  expiresInMinutes?: number | null;
}

export interface SchedulePublicBookingData {
  id?: number;
  bookingId?: number;
  bookingUid?: string;
  eventTypeId?: number;
  primaryHostUserId?: number;
  title?: string;
  publicKey?: string;
  lockKey?: string;
  inviteeTimezone?: string;
  hostTimezone?: string;
  status?: string;
  confirmationStatus?: string;
  startTime?: string;
  endTime?: string;
  locationType?: string | null;
  locationValue?: string | null;
  meetingLink?: string | null;
  source?: string;
  bookingChannel?: string;
  hosts?: SchedulePublicBookingHost[];
  [key: string]: unknown;
}

export interface SchedulePublicBookingHost {
  id?: number | string;
  userId?: number | string | null;
  hostUserId?: number | string | null;
  hostRole?: string | null;
  responseStatus?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  name?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  user?: {
    id?: number | string;
    name?: string | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    userName?: string | null;
  } | null;
  [key: string]: unknown;
}

export interface SchedulePublicBookingDetailsInvitee {
  id?: number | string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  timezone?: string | null;
  responseStatus?: string | null;
  crmContactId?: number | null;
  metadata?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface SchedulePublicBookingDetailsAnswer {
  id?: number | string;
  questionId?: number | string | null;
  fieldKey?: string | null;
  answerValue?: string | number | boolean | null;
  answerJson?: unknown;
  [key: string]: unknown;
}

export interface SchedulePublicBookingDetails {
  id?: number | string;
  bookingId?: number | string;
  bookingUid?: string;
  publicKey?: string;
  eventTypeId?: number;
  title?: string;
  startTime?: string;
  endTime?: string;
  inviteeStartTime?: string | null;
  inviteeEndTime?: string | null;
  inviteeTimezone?: string | null;
  hostTimezone?: string | null;
  status?: string;
  confirmationStatus?: string | null;
  locationType?: string | null;
  locationValue?: string | null;
  meetingLink?: string | null;
  invitee?: SchedulePublicBookingDetailsInvitee | null;
  primaryInvitee?: SchedulePublicBookingDetailsInvitee | null;
  invitees?: SchedulePublicBookingDetailsInvitee[];
  primaryHostUserId?: number;
  hosts?: SchedulePublicBookingHost[];
  answers?: SchedulePublicBookingDetailsAnswer[];
  [key: string]: unknown;
}

export interface SchedulePublicCreateBookingResult {
  booking: SchedulePublicBookingData;
  message: string;
}

export interface SchedulePublicRescheduleBookingRequest {
  newLockKey: string;
  inviteeEmail: string;
  reason: string;
  source: string;
  medium: string;
  campaign: string;
}

export interface SchedulePublicRescheduleBookingInput
  extends SchedulePublicRescheduleBookingRequest {
  publicKey: string;
  bookingId: string;
}

export interface SchedulePublicRescheduleBookingData {
  bookingUid?: string;
  previousBookingUid?: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  inviteeStartTime?: string;
  inviteeEndTime?: string;
  inviteeTimezone?: string;
  status?: string;
  confirmationStatus?: string;
  [key: string]: SchedulePublicJsonValue | undefined;
}

export interface SchedulePublicRescheduleBookingResult {
  booking: SchedulePublicRescheduleBookingData;
  message: string;
}

export interface SchedulePublicCancelBookingRequest {
  inviteeEmail: string;
  reason: string;
  source: string;
  medium: string;
  campaign: string;
}

export interface SchedulePublicCancelBookingInput
  extends SchedulePublicCancelBookingRequest {
  publicKey: string;
  bookingId: string;
}

export interface SchedulePublicCancelBookingData {
  bookingUid?: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  inviteeStartTime?: string;
  inviteeEndTime?: string;
  inviteeTimezone?: string;
  status?: string;
  confirmationStatus?: string;
  [key: string]: SchedulePublicJsonValue | undefined;
}

export interface SchedulePublicCancelBookingResult {
  booking: SchedulePublicCancelBookingData;
  message: string;
}

export type SchedulePublicBookingLinkResponse =
  SchedulingApiEnvelope<SchedulePublicBookingLink>;
export type SchedulePublicAvailableSlotsResponse =
  SchedulingApiEnvelope<SchedulePublicAvailableSlotsData>;
export type SchedulePublicAvailableDatesResponse =
  SchedulingApiEnvelope<SchedulePublicAvailableDatesData>;
export type SchedulePublicReserveSlotResponse =
  SchedulingApiEnvelope<SchedulePublicReserveSlotData>;
export type SchedulePublicCreateBookingResponse =
  SchedulingApiEnvelope<SchedulePublicBookingData>;
export type SchedulePublicSendEmailVerificationResponse =
  SchedulingApiEnvelope<SchedulePublicEmailVerificationData>;
export type SchedulePublicRescheduleBookingResponse =
  SchedulingApiEnvelope<SchedulePublicRescheduleBookingData>;
export type SchedulePublicCancelBookingResponse =
  SchedulingApiEnvelope<SchedulePublicCancelBookingData>;
export type SchedulePublicBookingDetailsResponse =
  SchedulingApiEnvelope<SchedulePublicBookingDetails>;

export type GetSchedulePublicBookingLinkInput =
  | string
  | {
      publicKey: string;
    };

export interface GetSchedulePublicAvailableSlotsInput {
  publicKey: string;
  date: string;
  timezone: string;
}

export interface GetSchedulePublicAvailableDatesInput {
  publicKey: string;
  from: string;
  to: string;
  timezone: string;
}

export interface GetSchedulePublicBookingDetailsInput {
  publicKey: string;
  bookingId: string;
  inviteeEmail: string;
}

export const SCHEDULE_PUBLIC_VALIDATION_MESSAGES = {
  PUBLIC_KEY_REQUIRED: "Booking link public key is required.",
  INVALID_PUBLIC_KEY: "Booking link public key is invalid.",
  DATE_REQUIRED: "Booking date is required.",
  INVALID_DATE: "Booking date must be in YYYY-MM-DD format.",
  FROM_DATE_REQUIRED: "A start date (from) is required.",
  TO_DATE_REQUIRED: "An end date (to) is required.",
  INVALID_DATE_RANGE: "The end date must be on or after the start date.",
  TIMEZONE_REQUIRED: "Timezone is required.",
  INVALID_TIMEZONE: "The selected timezone is not recognized.",
  START_TIME_REQUIRED: "Slot start time is required.",
  END_TIME_REQUIRED: "Slot end time is required.",
  INVALID_SLOT_TIME: "Slot time must use the selected slot timestamp.",
  INVITEE_EMAIL_REQUIRED: "Invitee email is required to reserve this slot.",
  INVALID_INVITEE_EMAIL: "Invitee email is invalid.",
  LOCK_KEY_REQUIRED: "Reserved slot lock key is required.",
  VERIFICATION_TOKEN_ID_REQUIRED: "Verification token id is required.",
  VERIFICATION_CODE_REQUIRED: "Verification code is required.",
  INVALID_VERIFICATION_TOKEN_ID: "Verification token id is invalid.",
  INVALID_VERIFICATION_CODE: "Verification code is invalid.",
  NEW_LOCK_KEY_REQUIRED: "New reserved slot lock key is required.",
  BOOKING_ID_REQUIRED: "Booking id is required.",
  INVALID_BOOKING_ID: "Booking id is invalid.",
  REASON_TOO_LONG: "Reason must be 500 characters or fewer.",
  INVITEE_REQUIRED: "At least one invitee is required.",
  INVALID_RESPONSE: "Scheduling public API returned an unexpected response.",
} as const;
